import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Upload, FileDown, Loader2, Columns3 } from "lucide-react";

const LABELS = {
  application_number: "Application Number", full_name: "Full Name", guardian_name: "Father's / Husband's Name",
  mobile: "Mobile Number", aadhaar: "Aadhaar Number", category: "Category", academic_year: "Academic Year",
  institution: "College / Institution", course: "Course", class_year: "Class / Year", taluka: "Taluka",
  location_type: "Location Type", eligibility_status: "Eligibility Status",
};

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("skip");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const reset = (f) => { setFile(f); setHeaders(null); setMapping({}); setPreview(null); setSummary(null); };

  const readHeaders = async () => {
    if (!file) return toast.error("Please choose an Excel or CSV file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/beneficiaries/import/headers", fd);
      setHeaders(data); setMapping(data.suggested_mapping);
      toast.success(`${data.headers.length} columns detected — please confirm the mapping`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doPreview = async () => {
    const missing = (headers?.required_fields || []).filter((f) => !mapping[f]);
    if (missing.length) return toast.error(`Please map required fields: ${missing.map((m) => LABELS[m] || m).join(", ")}`);
    setBusy(true); setSummary(null);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/beneficiaries/import/preview", fd);
      setPreview(data);
      toast.success(`${data.valid} valid rows, ${data.duplicates} duplicates, ${data.errors.length} errors`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/beneficiaries/import/confirm", { rows: preview.valid_rows, duplicate_mode: mode });
      setSummary(data); setPreview(null); setHeaders(null); setFile(null);
      toast.success(`Import completed: ${data.imported} imported, ${data.updated} updated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-page">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-[#0f2545] sm:text-4xl">Excel Import</h1>
        <p className="text-sm text-slate-500">एक्सेल आयात · upload → map columns → validate → preview → confirm</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <input data-testid="import-file" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => reset(e.target.files[0])} className="text-sm" />
        <button data-testid="import-headers-btn" disabled={busy} onClick={readHeaders} className="flex items-center gap-1.5 rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Read Columns
        </button>
        <button data-testid="import-template-btn" onClick={() => downloadFile("/beneficiaries/import/template", "swadhar_import_template.xlsx")} className="flex items-center gap-1.5 rounded-md border border-slate-300 px-4 py-2 text-sm">
          <FileDown size={15} /> Download Template
        </button>
        <select data-testid="import-dup-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
          <option value="skip">Duplicates: Skip</option>
          <option value="update">Duplicates: Update Existing</option>
          <option value="cancel">Duplicates: Cancel Import</option>
        </select>
      </div>

      {headers && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="mapping-panel">
          <div className="flex items-center gap-2">
            <Columns3 size={18} className="text-[#0f2545]" />
            <h3 className="text-base font-semibold text-[#0f2545] md:text-lg">Map Columns / स्तंभ जुळवा</h3>
          </div>
          <p className="text-xs text-slate-500">Match each system field to a column from your file. Fields marked * are required; leave others blank to skip.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {headers.system_fields.map((f) => {
              const required = headers.required_fields.includes(f);
              return (
                <label key={f} className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600">
                    {LABELS[f] || f} {required && <span className="text-rose-600">*</span>}
                  </span>
                  <select data-testid={`map-${f}`} value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                    className={`w-full rounded-md border px-2 py-2 text-sm ${required && !mapping[f] ? "border-rose-300 bg-rose-50" : "border-slate-300"}`}>
                    <option value="">— Not mapped —</option>
                    {headers.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                  {mapping[f] && headers.sample_row?.[mapping[f]] !== undefined && (
                    <span className="mt-1 block truncate text-[11px] text-slate-400">e.g. {String(headers.sample_row[mapping[f]])}</span>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="import-preview-btn" disabled={busy} onClick={doPreview} className="rounded-md bg-[#0f2545] px-4 py-2 text-sm text-white disabled:opacity-60">Validate & Preview</button>
            <button data-testid="mapping-auto" onClick={() => setMapping(headers.suggested_mapping)} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Auto-map Again</button>
            <button data-testid="mapping-clear" onClick={() => setMapping({})} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Clear Mapping</button>
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="import-preview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Total Rows", preview.total_rows], ["Valid", preview.valid], ["Duplicates", preview.duplicates], ["Errors", preview.errors.length]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 p-3"><div className="text-xl font-semibold text-[#0f2545]">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
            ))}
          </div>
          {preview.errors.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="import-errors">
              <div className="font-medium">Validation errors</div>
              <ul className="mt-1 list-inside list-disc text-xs">{preview.errors.slice(0, 20).map((e) => <li key={`err-${e.row}`}>Row {e.row}: {e.error}</li>)}</ul>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-[800px] w-full text-xs">
              <thead className="bg-slate-100 text-left"><tr>{["Row", "Application No.", "Name", "Mobile", "Taluka", "Institution", "Duplicate of"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr key={`row-${r._row}-${r.application_number}`} className="border-t border-slate-100">
                    <td className="p-2">{r._row}</td><td className="p-2">{r.application_number}</td><td className="p-2">{r.full_name}</td>
                    <td className="p-2">{r.mobile}</td><td className="p-2">{r.taluka}</td><td className="p-2">{r.institution}</td>
                    <td className="p-2 text-amber-700">{r._duplicate_of || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid="import-confirm-btn" disabled={busy} onClick={confirm} className="rounded-md bg-[#0f2545] px-5 py-2.5 text-sm text-white disabled:opacity-60">Confirm Import</button>
        </div>
      )}

      {summary && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="import-summary">
          <div className="font-semibold">Import Summary</div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {Object.entries(summary).map(([k, v]) => <div key={k}><div className="text-lg font-semibold">{v}</div><div className="text-xs capitalize">{k}</div></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
